// src/pages/GoogleSignInPage.jsx

import React, { useEffect, useState } from 'react';
import { signInWithRedirect } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/use-toast'; // Adjust path
import { Button } from '../../components/ui/button'; // Adjust path
import { useAuth } from '../../context/AuthContext'; // Import useAuth
import { Loader2 } from 'lucide-react';
import awsmobile from "../../aws-exports";


const GoogleSignInPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { logout, loading, token } = useAuth(); // Get the logout function from our context
  console.log("STAGE:", process.env.REACT_APP_STAGE);
  console.log("Redirect Sign-In:", awsmobile.Auth.Cognito.loginWith.oauth.redirectSignIn);

  useEffect(() => {
    if (!loading && token) {
      navigate('/admin/search', { replace: true });
    }
  }, [loading, navigate, token]);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      // Attempt to sign in
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      // --- START: PROACTIVE FIX ---
      // Check if the error is the specific "already logged in" exception
      if (error.name === 'UserAlreadyAuthenticatedException') {
        toast({
          variant: "destructive",
          title: "Session Conflict",
          description: "A stuck session was detected and cleared. Please try signing in again."
        });
        // Force a full logout to clear the zombie session
        await logout(); 
        setIsLoading(false); // Reset loading state
      } else {
        // Handle other potential errors
        console.error("Google sign-in initiation error:", error);
        toast({
          variant: "destructive",
          title: "Sign-In Error",
          description: "Could not start the Google sign-in process."
        });
        setIsLoading(false);
      }
      // --- END: PROACTIVE FIX ---
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4">
      <div className="mx-auto flex w-full max-w-sm flex-col items-center space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-bold">Admin Portal</h1>
          <p className="text-gray-500">Please sign in with your Google account to continue.</p>
        </div>
        <div className="w-full">
          <Button
            onClick={handleGoogleLogin}
            size="lg"
            className="w-full flex items-center justify-center gap-2 text-base py-3"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : (
              <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M22.56,12.25C22.56,11.47 22.49,10.72 22.36,10H12V14.26H17.94C17.64,15.91 16.74,17.31 15.24,18.26V21.09H19.09C21.29,19.12 22.56,15.97 22.56,12.25Z" fill="#4285F4"/><path d="M12,23C14.97,23 17.45,22.04 19.09,20.09L15.24,17.26C14.24,17.91 13.1,18.25 12,18.25C9.43,18.25 7.28,16.66 6.46,14.42H2.59V17.31C4.22,20.78 7.83,23 12,23Z" fill="#34A853"/><path d="M6.46,13.58C6.19,12.81 6.05,11.95 6.05,11C6.05,10.05 6.19,9.19 6.46,8.42V5.53H2.59C1.41,7.79 0.75,10.09 0.75,12.5C0.75,14.91 1.41,17.21 2.59,19.47L6.46,16.58V13.58Z" fill="#FBBC05"/><path d="M12,5.75C13.34,5.75 14.52,6.21 15.49,7.13L19.16,3.54C17.45,1.99 14.97,1 12,1C7.83,1 4.22,3.22 2.59,6.69L6.46,9.58C7.28,7.34 9.43,5.75 12,5.75Z" fill="#EA4335"/></svg>
            )}
            Sign in with Google
          </Button>
        </div>
        {/* --- START: MANUAL FIX (ESCAPE HATCH) --- */}
        <div className="text-sm text-gray-500">
          Stuck?{" "}
          <button
            onClick={() => logout(() => toast({ title: "Session cleared.", description: "You have been signed out." }))}
            className="font-semibold text-primary underline hover:text-primary/80"
          >
            Click here to sign out completely.
          </button>
        </div>
        {/* --- END: MANUAL FIX --- */}
      </div>
    </div>
  );
};

export default GoogleSignInPage;
